import { Fab } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import Icon from '@material-ui/core/Icon';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import lodash from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { IUserSession } from '../../../@types/employee';
import { ITransformer } from '../../../@types/model/transformer/transformer';
import TransformerLpuReadingHelper, { ITransformerLpuReading } from '../../../@types/model/transformer/reading/transformerLpuReading';
import { IRootState } from '../../../@types/redux';
import AppFunctionsService from '../../../services/appFunctionsService';
import GeneralFunctions from '../../../store/general/functions';
import DateRangePicker from '../../customComponents/input/DateRangePicker';
import MaterialTable from '../../customComponents/materialTable/Table';
import TransformerDropdown from '../../customComponents/selector/transformer/TransformerSelector';
import EditTransformerLpuReadingEdit from '../dialog/EditLpuReading';
import TransformerLPUReadingDeleteDialog from './dialog/reading/lpu/LPUMeterReadingDeleteDialog';
import Screen from '../../customComponentV2/screen/Screen';

interface ITransformerLPUMeterListProps {
    isTransformerAdmin : boolean;
    session ?: IUserSession | null;
}

const TransformerLPUMeterListClass = (props : ITransformerLPUMeterListProps) : React.ReactElement => {
    const [transformerNumber, setTransformerNumber] = useState<string | undefined>(undefined);
    const [readings, setReadings] = useState<Array<ITransformerLpuReading>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dateFrom, setDateFrom] = useState<moment.Moment | null>(null);
    const [dateTo, setDateTo] = useState<moment.Moment | null>(null);
    const [errorOnly, setErrorOnly] = useState<boolean>(false);

    useEffect(() => {
        const session = props.session;

        if (!session) {
            return;
        }

        setIsLoading(true);

        const unsubscribe = TransformerLpuReadingHelper.listen()
            .limit(1000)
            .onSnapshot((snapshot) => {
            
            const fetchedDocs : Array<ITransformerLpuReading> = [];

            snapshot.forEach((doc) => {
                fetchedDocs.push(doc.data());
            });

            const filteredReadings = fetchedDocs.filter(x => 
                session.employee.IsTransformerAdmin || session.employee.Areas.includes(x.division ?? '')
            );

            setReadings(filteredReadings);
            setIsLoading(false);
        }, (err) => {
            GeneralFunctions.generalShowErrorSnackbar(err.message);
            setIsLoading(false);
        });

        return () => {
            unsubscribe();
        };
    }, [props.session]);

    const getFilteredData = useMemo<Array<ITransformerLpuReading>>(() => {
        return lodash
            .chain(readings)
            .filter((n: ITransformerLpuReading) => !errorOnly || !!n.isDuplicate || !!n.hasPeakUsageError || !!n.hasOffPeakUsageError || !!n.hasStandardUsageError)
            .filter((n: ITransformerLpuReading) => !transformerNumber || n.transformerRef.includes(transformerNumber))
            .filter((n: ITransformerLpuReading) => !dateFrom || n.date >= dateFrom.valueOf())
            .filter((n: ITransformerLpuReading) => !dateTo || n.date <= dateTo.valueOf())
            .sort((a: ITransformerLpuReading, b: ITransformerLpuReading) => b.date - a.date)
            .value();
    }, [transformerNumber, dateFrom, dateTo, readings, errorOnly]);

    const onDelete = async (reading : ITransformerLpuReading) : Promise<void> => {
        const session = props.session;

        if (!session?.employee.IsTransformerAdmin) return;

        await TransformerLpuReadingHelper.delete(reading.id);
    };

    const onEPNumberChange = (value ?: ITransformer) : void => {
        setTransformerNumber(value?.EPNumber);
    };

    const onDateSelectionChange = (start : moment.Moment | null, end : moment.Moment | null) : void => {
        setDateFrom(!start ? null : moment(start));
        setDateTo(!end ? null : moment(end));
    };

    const onShowOnlyErrorClick = () : void => {
        setErrorOnly(!errorOnly);
    };

    const { isTransformerAdmin, session } = props;
    const filteredReadings = getFilteredData;
    
    return (
        <Screen
            useAnimatedLoader
            isPadded={false}
            isScrollable={false}
            backgroundColor='bcw'>
            <div className={'fdc flx1 p10 mh0 mw0 bcg0'}>
                <Toolbar>
                    <span className={'flx1'} />
                    <div className='pr20'>
                        <Tooltip title='Show only Errors'>
                            <Fab color={errorOnly ? 'primary' : 'default'} size='small' onClick={onShowOnlyErrorClick}>
                                <Icon className='fs20'>
                                    warning
                                </Icon>
                            </Fab>
                        </Tooltip>
                    </div>
                    <div className={'aic p5 mb10'}>
                        <DateRangePicker start={dateFrom} end={dateTo} onChange={onDateSelectionChange} />
                    </div>
                    <div className={'fdr pt5 jcfe aic'}>
                        <div className='fdc flx1 w240'>
                            <TransformerDropdown 
                                value={transformerNumber}
                                label={'Select Transformer'}
                                fullWidth
                                onChange={onEPNumberChange}
                            />
                        </div>
                    </div>
                </Toolbar>
                <Card className={'flx1 fdc mb70'}>
                    <MaterialTable<ITransformerLpuReading>
                        id='transformerReadingsTable'
                        data={filteredReadings}
                        isLoading={isLoading}
                        rowsPerPage={50}
                        externalSort
                        columns={[{
                            header: '',
                            field: 'EPNumber',
                            width: 250,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (
                                <div className=' fdr aic'>
                                    <EditTransformerLpuReadingEdit
                                        key={`${row.id}_edit`} fullWidth
                                        maxWidth='md'
                                        reading={row}
                                        disabled={!isTransformerAdmin && (row.createdBy !== session?.firebaseUser.uid)}
                                        transformerRef={row.transformerRef}
                                    />
                                    {
                                        (isTransformerAdmin || (row.createdBy === session?.firebaseUser.uid)) &&
                                        <TransformerLPUReadingDeleteDialog key={`${row.id}_delete`} 
                                            reading={row}
                                            onDelete={onDelete}
                                        />
                                    }
                                    {
                                        !!row.isWeb &&
                                        <Icon className='cp ml10'>
                                            laptop
                                        </Icon>                                            
                                    }
                                    {
                                        !row.isWeb &&
                                        <Icon className='cp ml10'>
                                            smartphone
                                        </Icon>                                            
                                    }
                                    {
                                        row.isDuplicate &&
                                        <Tooltip title='Duplicate'>
                                            <Icon className='cy ml10'>
                                                warning
                                            </Icon>    
                                        </Tooltip>                                        
                                    }
                                    {
                                        row.hasPeakUsageError &&
                                        <Tooltip title='Peak Usage Incorrect'>
                                            <Icon className='cy ml10'>
                                                warning
                                            </Icon>    
                                        </Tooltip>                                        
                                    }
                                    {
                                        row.hasOffPeakUsageError &&
                                        <Tooltip title='Off Peak Usage Incorrect'>
                                            <Icon className='cy ml10'>
                                                warning
                                            </Icon>    
                                        </Tooltip>                                        
                                    }
                                    {
                                        row.hasStandardUsageError &&
                                        <Tooltip title='Standard Usage Incorrect'>
                                            <Icon className='cy ml10'>
                                                warning
                                            </Icon>    
                                        </Tooltip>                                        
                                    }
                                </div>
                            ),
                        }, {
                            header: 'Date',
                            field: 'date',
                            width: 150,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (<span>{AppFunctionsService.formatDateTimeToDateOnly(row.date)}</span>),
                        }, {
                            header: 'EP Point',
                            field: 'transformerRef',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Area',
                            field: 'division',
                            width: 160,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Group',
                            field: 'group',
                            width: 160,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Peak Reading',
                            field: 'peakReading',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (
                                <b>
                                    {row.peakReading}
                                </b>
                            ),
                        }, {
                            header: 'Prev. Peak Reading',
                            field: 'prevPeakReading',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Peak Usage',
                            field: 'peakUsage',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Standard Reading',
                            field: 'standardReading',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (
                                <b>
                                    {row.standardReading}
                                </b>
                            ),
                        }, {
                            header: 'Prev. Standard Reading',
                            field: 'prevStandardReading',
                            width: 210,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Standard Usage',
                            field: 'standardUsage',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Off Peak Reading',
                            field: 'offPeakReading',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (
                                <b>
                                    {row.offPeakReading}
                                </b>
                            ),
                        }, {
                            header: 'Prev. Off Peak Reading',
                            field: 'prevOffPeakReading',
                            width: 210,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Off Peak Usage',
                            field: 'offPeakUsage',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Total Usage',
                            field: 'maxDemand',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (
                                <span>
                                    {row.peakUsage + row.offPeakUsage + row.standardUsage}
                                </span>
                            ),
                        }, {
                            header: 'Max Demand',
                            field: 'maxDemand',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Max Demand Date',
                            field: 'dateOfMaxDemand',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (<span>{AppFunctionsService.formatDateTimeToDateOnly(row.dateOfMaxDemand)}</span>),
                        }, {
                            header: 'Max Demand Time',
                            field: 'timeOfMaxDemand',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Captured By',
                            field: 'createdByName',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (
                                <Tooltip title={row.createdByEmployee}>
                                    <div>
                                        {
                                            row.createdByName
                                        }
                                    </div>
                                </Tooltip>
                            ),
                        }, {
                            header: 'Captured On',
                            field: 'createdOn',
                            width: 175,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: (row: ITransformerLpuReading) : React.ReactElement => (<span>{AppFunctionsService.formatDateTimeToDateOnly(row.createdOn)}</span>),
                        }]}
                    />
                </Card>
                <div className='fdr jcfe'>
                    <EditTransformerLpuReadingEdit
                        fullWidth
                        maxWidth='md'
                    
                    />
                </div>
            </div>
        </Screen>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        isTransformerAdmin: !!state.auth.session?.employee.IsTransformerAdmin,
        session: state.auth.session,
    };
};

const TransformerLPUMeterList = connect(
    mapStateToProps,
)(TransformerLPUMeterListClass);

export default TransformerLPUMeterList;
